// src/Components/Pages/Team.jsx (Complete with Read More and Local Images)
import { useState } from "react";
import { Link } from "react-router-dom";
import { 
  FaUsers, 
  FaEnvelope, 
  FaPhone
} from "react-icons/fa";
import { motion } from "framer-motion";

// Import images from src/assets/images (correct path: go up two levels)
import HalimaImage from "../../assets/images/Halima.jpeg";
import MosesImage from "../../assets/images/Moses.jpeg";
import ElizabethImage from "../../assets/images/Elizabeth.jpeg";
import ValarieImage from "../../assets/images/Valarie.jpeg";
import ShariffaImage from "../../assets/images/Shariffa.jpeg";
import SalimImage from "../../assets/images/Salim.jpeg";

function Team() {
  const team = [
    {
      name: "Halima Nyota",
      role: "HALLIMA NYOTA: FOUNDER & EXECUTIVE DIRECTOR",
      fullText: `Executive Director: Halima Nyota. Leading the organization's vision and strategic direction in empowering communities across Kakamega. Hallima Nyota is the Founder and Executive Director of Rising to Greatness Organization (RIGO), a Kenyan grassroots, women- and youth-led organization dedicated to Transforming Communities through human rights, gender equality, peacebuilding, and inclusive development. She is an accomplished human rights defender, peacebuilder, and social justice leader with extensive experience designing and leading community-driven programmes that strengthen access to justice, prevent gender-based violence, advance sexual and reproductive health and rights (SRHR), and expand economic opportunities for women and young people.

Under Hallima's leadership, RIGO has built trusted partnerships with grassroots movements, government institutions, civil society organizations, and international development partners to deliver evidence-based, locally led solutions in underserved communities, particularly within artisanal mining regions. She has successfully mobilized multi-stakeholder collaborations, influenced policy and governance processes, and strengthened the capacity of community-based actors to drive sustainable social change.

Beyond RIGO, Hallima contributes to national, regional, and global dialogues on human rights, digital governance, feminist leadership, and peacebuilding, advocating for policies that center the voices and experiences of marginalized communities. Her leadership combines strategic vision with deep community engagement, ensuring that local knowledge shapes impactful programmes, accountable institutions, and lasting development outcomes.With over 10 years of experience in community development, Halima champions women's rights and youth empowerment in Western Kenya.`,
      image: HalimaImage,
      imagePosition: "center 20%"
    },
    {
      name: "Moses Okanya",
      role: "Finance Manager",
      fullText: `Finance Manager: Moses Okanya. Moses Okanya is a seasoned Finance Manager with vast experience in financial management and organizational financial operations. He has a proven track record in preparing accurate financial reports for various donors, developing organizational budgets, formulating financial policies, and providing strategic financial advice to the organization. His responsibilities also include making payments, examining payment vouchers for completeness, preparing statutory deductions, filing statutory returns, and compiling comprehensive audit files to support accountability and compliance. Moses holds a Bachelor's Degree in Economics, is a Certified Public Accountant (CPA III), and has a Diploma in Business Administration. He is currently pursuing a Master's Degree in Project Planning and Management, further enhancing his expertise in financial and project management.`,
      image: MosesImage,
      imagePosition: "center 30%"
    },
    {
      name: "Elizabeth Mwangi",
      role: "Programs Manager",
      fullText: `PROGRAMS MANAGER: Elizabeth Mwangi. Elizabeth Mwangi serves as Programs Manager at Rising to Greatness Organization (RIGO), where she leads the planning, implementation, and coordination of initiatives advancing gender equality, climate action, youth empowerment, and sustainable development in Kakamega and across western Kenya. She collaborates closely with the dedicated team at RIGO to design transformative programs that address issues, including gender-based violence, mental health rights, teenage pregnancy prevention, climate change, and the socioeconomic impacts of artisanal mining. In her additional role as Monitoring and Evaluation Officer, Elizabeth ensures that all projects remain evidence-based, accountable, and impactful. This involves developing M&E frameworks, tracking progress against relevant Sustainable Development Goals, collecting and analyzing data, conducting field assessments, and producing comprehensive reports that inform decision-making and highlight the organization's contributions to community resilience and justice.`,
      image: ElizabethImage,
      imagePosition: "center 20%"
    },
    {
      name: "Said N. Salim",
      role: "Partnerships and Resource Mobilization Officer",
      fullText: `Said Salim is a Global Health professional and development practitioner with a strong focus on community empowerment, partnership building, and resource mobilization. With a background in Global Health and Travel Medicine, his work centers on strengthening collaborations that support youth development, sustainable livelihoods, and inclusive community programs.

He brings extensive experience in mentorship, enterprise development, and business coaching, particularly supporting young entrepreneurs to build resilient and market-oriented enterprises. Said is also a skilled communications specialist with expertise in digital media, graphic design, and strategic storytelling—helping organizations effectively communicate impact, engage stakeholders, and mobilize support for transformative initiatives. Through his work, he is committed to fostering partnerships and solutions that drive sustainable community-led change.`,
      image: SalimImage,
      imagePosition: "center 25%"
    },
    {
      name: "Valarie Manyali",
      role: "Community Outreach Coordinator",
      fullText: `Community Outreach Coordinator: Valarie Manyali. Valarie Manyali is a Community Outreach Coordinator at Rising to Greatness Organization, dedicated to empowering communities through inclusive engagement and advocacy, with proven experience in coordinating outreach initiatives, stakeholder partnerships, and community trainings that advance gender equality, promote sexual and reproductive health rights, prevent gender-based violence, and strengthen meaningful community participation. She is passionate about community-led solutions and works closely with local stakeholders to ensure programs are inclusive, responsive, and sustainable.`,
      image: ValarieImage,
      imagePosition: "center 15%"
    },
    {
      name: "Shariffa Amolo",
      role: "Communications & Data Protection Officer",
      fullText: `Communications Officer/Data Protection Officer: Shariffa Amolo. Shariffa who is the communication officer is also a certified Data Protection Officer. As the Communication Officer her mandate is to be the liaison person between the organization and the external stakeholders, ensure consistency in terms of voicing, branding, messaging and frequency of posting via digital channels and monitor and analyze social media metrics: Track and measure the performance of social media campaigns using analytics tools, providing insights and recommendations for improvement. Under the docket of data protection officer, her work is to ensure there is utmost data protection, advising the organization on data compliance, monitoring compliance with the act, handling data subject rights, and data breach management.`,
      image: ShariffaImage,
      imagePosition: "center 25%"
    }
  ];

  // Fixed: Show all 6 team members
  const firstRow = team.slice(0, 3);  // First 3 members
  const secondRow = team.slice(3, 6); // Last 3 members

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0 }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  // Team Member Card Component with Read More
  const TeamMemberCard = ({ member }) => {
    const [expanded, setExpanded] = useState(false);
    const [imageError, setImageError] = useState(false);

    // Handle image error
    const handleImageError = () => {
      setImageError(true);
      console.log(`Failed to load image for ${member.name}`);
    };

    return (
      <motion.div
        variants={fadeInUp}
        whileHover={{ y: -5 }}
        className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 p-6 text-left h-full flex flex-col"
      >
        {/* Circular Image and Header */}
        <div className="flex items-center gap-4 mb-4">
          <div className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden border-4 border-primaryGold/20 hover:border-primaryGold transition-all duration-300 flex-shrink-0 bg-gray-100">
            {!imageError ? (
              <div className="w-full h-full relative">
                <img 
                  src={member.image}
                  alt={member.name}
                  className="w-full h-full"
                  style={{ 
                    objectFit: 'cover',
                    objectPosition: member.imagePosition || 'center 20%'
                  }}
                  onError={handleImageError}
                />
              </div>
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-primaryBlue text-white text-2xl font-bold">
                {member.name.charAt(0)}
              </div>
            )}
          </div>
          
          <div className="flex-1">
            <h3 className="text-lg md:text-xl font-bold text-primaryBlue">{member.name}</h3>
            <p className="text-primaryGold text-sm md:text-base font-medium">{member.role}</p>
          </div>
        </div>

        {/* Text with Read More */}
        <div className="text-gray-700 text-sm leading-relaxed mb-4 flex-1">
          <p className="whitespace-pre-line">
            {expanded ? member.fullText : `${member.fullText.substring(0, 180)}...`}
          </p>
          <button 
            onClick={() => setExpanded(!expanded)}
            className="text-primaryGold hover:text-yellow-400 font-semibold text-xs mt-2 focus:outline-none transition-colors duration-200"
          >
            {expanded ? 'Read Less' : 'Read More'}
          </button>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white overflow-hidden">
      {/* Hero Section */}
      <section className="relative bg-primaryBlue text-white py-16 md:py-20 overflow-hidden">
        <motion.div 
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 0.1, scale: 1 }}
          transition={{ duration: 1.5 }}
          className="absolute top-0 left-0 w-64 h-64 bg-primaryGold rounded-full -translate-x-1/2 -translate-y-1/2"
        />
        <motion.div 
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 0.1, scale: 1 }}
          transition={{ duration: 1.5, delay: 0.3 }}
          className="absolute bottom-0 right-0 w-96 h-96 bg-primaryGold rounded-full translate-x-1/3 translate-y-1/3"
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div 
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center"
          >
            <motion.div
              variants={fadeInUp}
              className="inline-block p-3 md:p-4 bg-primaryGold/20 rounded-full mb-4"
            >
              <FaUsers className="text-2xl md:text-4xl text-primaryGold" />
            </motion.div>
            <motion.h1 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4"
            >
              Our <span className="text-primaryGold">Team</span>
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              className="text-base sm:text-lg max-w-3xl mx-auto text-gray-100 px-4"
            >
              Meet the dedicated professionals behind Rising to Greatness
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Team Grid - 3 on top, 3 on bottom with Read More */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          {/* First Row - 3 members */}
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-6 mb-6"
          >
            {firstRow.map((member, index) => (
              <TeamMemberCard key={index} member={member} />
            ))}
          </motion.div>

          {/* Second Row - 3 members */}
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-6"
          >
            {secondRow.map((member, index) => (
              <TeamMemberCard key={index} member={member} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* Join Us Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-primaryBlue to-primaryGold">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-2xl md:text-3xl font-bold text-white mb-4"
            >
              Join Our Team
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-white/90 mb-6"
            >
              Passionate about community development? Get in touch with us.
            </motion.p>
            <motion.div 
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-3 justify-center"
            >
              <Link
                to="/contact"
                className="bg-white text-primaryBlue px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Contact Us
              </Link>
              <Link
                to="/about"
                className="bg-transparent border-2 border-white text-white px-6 py-3 rounded-full font-semibold hover:bg-white/10 transition-all duration-300"
              >
                Learn More
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Decorative Footer */}
      <div className="h-2 bg-gradient-to-r from-primaryBlue via-primaryGold to-primaryBlue"></div>
    </div>
  );
}

export default Team;